import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import BlockIcon from '@mui/icons-material/Block';
import EditIcon from '@mui/icons-material/Edit';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import VpnKeyOutlinedIcon from '@mui/icons-material/VpnKeyOutlined';
import * as Sentry from '@sentry/react';
import {
  apiKeyIpMode,
  apiKeyIpModeLabel,
  apiKeyStatus,
  apiKeyStatusLabel,
  type ApiKeyIpMode,
} from '../../../constants/enum';
import type { ApiKeyListItem } from '../../../types/apiKey';
import {
  listApiKeys,
  revokeApiKey,
  updateApiKeyIpAllowlist,
} from '../../../api/firebase/function/apiKeyFunction';
import {
  gray10,
  gray20,
  gray40,
  gray60,
  gray80,
  gray100,
  green20,
  green50,
  primaryButtonBackgroundColor,
  red20,
  red50,
  secondaryButtonBackgroundColor,
  tertiaryButtonBackgroundColor,
  yellow50,
  yellow_overlay10,
} from '../../../constants/colors';
import {
  dialogText,
  dialogTitle,
  regularMd,
  regularSm,
  sectionTitle,
} from '../../../constants/location';
import CustomLoading from '../../uiParts/form/CustomLoading';
import ApiKeyCreateDialog from './ApiKeyCreateDialog';
import ApiKeyRotateDialog from './ApiKeyRotateDialog';
import ApiKeyIpWhitelistEditor from './ApiKeyIpWhitelistEditor';

interface Props {
  /** 運営スタッフが他社を操作する場合は company_id を渡す */
  companyId?: string;
  /** 編集権限の有無（false の場合は閲覧のみ） */
  readOnly?: boolean;
  /**
   * 発行ダイアログの開閉を親（ページヘッダーの発行ボタン）から制御する場合に渡す。
   * 渡した場合はセクション内の発行ボタンを表示しない。
   */
  createOpen?: boolean;
  /** createOpen を渡したときに、ダイアログを閉じる要求を親へ通知する */
  onCreateClose?: () => void;
}

const STATUS_BADGE_STYLES: Record<number, { background: string; color: string }> = {
  [apiKeyStatus.valid]: { background: green20, color: green50 },
  [apiKeyStatus.rotating]: { background: yellow_overlay10, color: yellow50 },
  [apiKeyStatus.revoked]: { background: gray20, color: gray80 },
};

const styles = {
  sectionHeader: {
    alignItems: 'center',
    display: 'flex',
    justifyContent: 'flex-end',
    marginBottom: '16px',
  },
  buttonLarge: {
    height: '44px',
    padding: '0 16px',
    '& .MuiButton-icon': {
      marginRight: '4px',
    },
  },
  buttonSmall: {
    height: '36px',
    minWidth: 'auto',
    padding: '0 12px',
    fontSize: '12px',
  },
  errorText: {
    ...regularSm,
    color: red50,
    marginBottom: '16px',
  },
  tableWrapper: {
    overflowX: 'auto',
    width: '100%',
    WebkitOverflowScrolling: 'touch',
  },
  table: {
    minWidth: '960px',
    width: '100%',
  },
  tableHeadRow: {
    height: '48px',
  },
  tableHeadText: {
    ...regularSm,
    color: gray80,
    padding: '0 16px',
    textAlign: 'left' as const,
    whiteSpace: 'nowrap' as const,
  },
  tableDataRow: {
    height: '48px',
  },
  tableDataRowRevoked: {
    backgroundColor: gray10,
  },
  tableDataText: {
    ...regularSm,
    color: gray100,
    padding: '0 16px',
    textAlign: 'left' as const,
    whiteSpace: 'nowrap' as const,
  },
  emptyWrapper: {
    alignItems: 'center',
    display: 'flex',
    flexDirection: 'column' as const,
    justifyContent: 'center',
    padding: '64px 16px',
    textAlign: 'center' as const,
  },
  emptyIcon: {
    color: gray60,
    height: '56px',
    marginBottom: '24px',
    width: '56px',
  },
  emptyTitle: {
    ...sectionTitle,
    marginBottom: '8px',
  },
  emptyDescription: {
    ...regularMd,
    color: gray80,
  },
  actionCell: {
    padding: '0 16px',
    textAlign: 'right' as const,
    whiteSpace: 'nowrap' as const,
  },
  actionWrapper: {
    display: 'inline-flex',
    gap: '8px',
    alignItems: 'center',
  },
  statusBadge: {
    ...regularSm,
    display: 'inline-flex',
    alignItems: 'center',
    height: '24px',
    padding: '0 8px',
    borderRadius: '4px',
    fontSize: '12px',
  },
  dialog: {
    '& .MuiPaper-root': {
      border: '1px solid' + gray40,
      boxSizing: 'border-box',
      margin: '16px',
      padding: '40px',
      width: '480px',
      '@media screen and (max-width:960px)': {
        padding: '32px 16px',
      },
    },
  },
  dialogHeader: {
    alignItems: 'center',
    display: 'flex',
    justifyContent: 'space-between',
    marginBottom: '24px',
  },
  dialogTitle,
  dialogBodyText: {
    ...dialogText,
    marginBottom: '24px',
  },
  dialogErrorBox: {
    backgroundColor: red20,
    border: `1px solid ${red50}`,
    borderRadius: '4px',
    color: red50,
    fontSize: '14px',
    padding: '8px 12px',
    marginBottom: '16px',
  },
  dialogActions: {
    display: 'flex',
    gap: '12px',
    justifyContent: 'flex-end',
  },
  dialogActionButton: {
    height: '44px',
    minWidth: '120px',
  },
};

const formatDate = (date: Date | null): string => {
  if (!date) return '-';
  return date.toLocaleString();
};

const ApiKeyManagement: React.FC<Props> = ({
  companyId,
  readOnly = false,
  createOpen: createOpenProp,
  onCreateClose,
}) => {
  // 発行ダイアログの開閉を親が制御するかどうか
  const isCreateControlled = createOpenProp !== undefined;
  const [keys, setKeys] = useState<ApiKeyListItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [errorText, setErrorText] = useState('');
  const [createOpenInternal, setCreateOpenInternal] = useState(false);
  const createOpen = isCreateControlled ? createOpenProp : createOpenInternal;
  const closeCreateDialog = (): void => {
    if (isCreateControlled) {
      onCreateClose?.();
    } else {
      setCreateOpenInternal(false);
    }
  };
  const [rotateTarget, setRotateTarget] = useState<ApiKeyListItem | null>(null);
  const [ipEditTarget, setIpEditTarget] = useState<ApiKeyListItem | null>(null);
  const [revokeTarget, setRevokeTarget] = useState<ApiKeyListItem | null>(null);
  const [revokeError, setRevokeError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const fetchList = useCallback(async (): Promise<void> => {
    setLoading(true);
    setErrorText('');
    try {
      const list = await listApiKeys(companyId);
      setKeys(list);
    } catch (e) {
      Sentry.captureException(e);
      const message = e instanceof Error ? e.message : 'APIキー一覧の取得に失敗しました。';
      setErrorText(message);
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  const handleRevoke = async (): Promise<void> => {
    if (!revokeTarget) return;
    setSubmitting(true);
    setRevokeError('');
    try {
      await revokeApiKey({
        company_id: companyId,
        api_key_id: revokeTarget.api_key_id,
      });
      setRevokeTarget(null);
      await fetchList();
    } catch (e) {
      Sentry.captureException(e);
      const message = e instanceof Error ? e.message : '無効化に失敗しました。';
      setRevokeError(message);
    } finally {
      setSubmitting(false);
    }
  };

  const closeRevokeDialog = (): void => {
    if (submitting) return;
    setRevokeTarget(null);
    setRevokeError('');
  };

  const handleSaveIpAllowlist = async (ipList: string[], ipMode: ApiKeyIpMode): Promise<void> => {
    if (!ipEditTarget) return;
    await updateApiKeyIpAllowlist({
      company_id: companyId,
      api_key_id: ipEditTarget.api_key_id,
      ip_allowlist: ipList,
      ip_mode: ipMode,
    });
    setIpEditTarget(null);
    await fetchList();
  };

  return (
    <Box>
      {/* 発行ボタンを内部で持つ場合（親ヘッダーに発行ボタンを置かないケース）のみ表示 */}
      {!readOnly && !isCreateControlled && (
        <Box sx={styles.sectionHeader}>
          <Button
            startIcon={<AddIcon sx={{ height: '18px', width: '18px' }} />}
            onClick={(): void => setCreateOpenInternal(true)}
            disabled={loading}
            sx={[primaryButtonBackgroundColor, styles.buttonLarge]}
          >
            APIキーを発行
          </Button>
        </Box>
      )}

      {errorText && <Typography sx={styles.errorText}>{errorText}</Typography>}

      {loading ? (
        <CustomLoading loading />
      ) : keys.length === 0 ? (
        <Box sx={styles.emptyWrapper}>
          <VpnKeyOutlinedIcon sx={styles.emptyIcon} />
          <Typography sx={styles.emptyTitle}>APIキーがまだありません</Typography>
          <Typography sx={styles.emptyDescription}>
            {readOnly
              ? 'APIキーはまだ発行されていません。'
              : '右上の「APIキーを発行」ボタンから発行してください。'}
          </Typography>
        </Box>
      ) : (
        <Box sx={styles.tableWrapper}>
          <Table sx={styles.table}>
            <TableHead>
              <TableRow sx={styles.tableHeadRow}>
                <TableCell sx={styles.tableHeadText}>表示名</TableCell>
                <TableCell sx={styles.tableHeadText}>環境</TableCell>
                <TableCell sx={styles.tableHeadText}>末尾4桁</TableCell>
                <TableCell sx={styles.tableHeadText}>ステータス</TableCell>
                <TableCell sx={styles.tableHeadText}>IP制限</TableCell>
                <TableCell sx={styles.tableHeadText}>有効期限</TableCell>
                <TableCell sx={styles.tableHeadText}>最終利用</TableCell>
                <TableCell sx={styles.tableHeadText}>発行日時</TableCell>
                {!readOnly && <TableCell sx={styles.tableHeadText}></TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {keys.map((k): JSX.Element => {
                const badge =
                  STATUS_BADGE_STYLES[k.status] ?? STATUS_BADGE_STYLES[apiKeyStatus.revoked];
                const isRevoked = k.status === apiKeyStatus.revoked;
                return (
                  <TableRow
                    key={k.api_key_id}
                    sx={[styles.tableDataRow, isRevoked && styles.tableDataRowRevoked]}
                  >
                    <TableCell sx={styles.tableDataText}>{k.display_name}</TableCell>
                    <TableCell sx={styles.tableDataText}>{k.environment}</TableCell>
                    <TableCell sx={styles.tableDataText}>...{k.key_last4}</TableCell>
                    <TableCell sx={styles.tableDataText}>
                      <Box
                        component="span"
                        sx={[
                          styles.statusBadge,
                          { backgroundColor: badge.background, color: badge.color },
                        ]}
                      >
                        {apiKeyStatusLabel[k.status] ?? '-'}
                      </Box>
                    </TableCell>
                    <TableCell sx={styles.tableDataText}>
                      {k.ip_mode === apiKeyIpMode.custom
                        ? `専用 ${k.ip_allowlist.length} 件`
                        : apiKeyIpModeLabel[k.ip_mode]}
                    </TableCell>
                    <TableCell sx={styles.tableDataText}>{formatDate(k.expires_at)}</TableCell>
                    <TableCell sx={styles.tableDataText}>{formatDate(k.last_used_at)}</TableCell>
                    <TableCell sx={styles.tableDataText}>{formatDate(k.created_at)}</TableCell>
                    {!readOnly && (
                      <TableCell sx={styles.actionCell}>
                        <Box sx={styles.actionWrapper}>
                          <Tooltip title="IP制限を編集">
                            <span>
                              <Button
                                onClick={(): void => setIpEditTarget(k)}
                                disabled={isRevoked}
                                startIcon={<EditIcon sx={{ height: '16px', width: '16px' }} />}
                                sx={[secondaryButtonBackgroundColor, styles.buttonSmall]}
                              >
                                IP
                              </Button>
                            </span>
                          </Tooltip>
                          <Tooltip title="再生成">
                            <span>
                              <Button
                                onClick={(): void => setRotateTarget(k)}
                                disabled={isRevoked}
                                startIcon={<RefreshIcon sx={{ height: '16px', width: '16px' }} />}
                                sx={[secondaryButtonBackgroundColor, styles.buttonSmall]}
                              >
                                再生成
                              </Button>
                            </span>
                          </Tooltip>
                          <Tooltip title="無効化">
                            <span>
                              <Button
                                onClick={(): void => setRevokeTarget(k)}
                                disabled={isRevoked}
                                startIcon={<BlockIcon sx={{ height: '16px', width: '16px' }} />}
                                sx={[tertiaryButtonBackgroundColor, styles.buttonSmall]}
                              >
                                無効化
                              </Button>
                            </span>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    )}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Box>
      )}

      <ApiKeyCreateDialog
        open={createOpen}
        companyId={companyId}
        onClose={closeCreateDialog}
        onCreated={fetchList}
      />

      <ApiKeyRotateDialog
        open={rotateTarget !== null}
        target={rotateTarget}
        companyId={companyId}
        onClose={(): void => setRotateTarget(null)}
        onRotated={fetchList}
      />

      {ipEditTarget && (
        <ApiKeyIpWhitelistEditor
          open
          initialAllowlist={ipEditTarget.ip_allowlist}
          initialMode={ipEditTarget.ip_mode}
          onClose={(): void => setIpEditTarget(null)}
          onSave={handleSaveIpAllowlist}
        />
      )}

      <Dialog open={revokeTarget !== null} onClose={closeRevokeDialog} sx={styles.dialog}>
        <Box sx={styles.dialogHeader}>
          <Typography sx={styles.dialogTitle}>APIキーを無効化しますか？</Typography>
          <IconButton onClick={closeRevokeDialog} disabled={submitting} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
        <Typography sx={styles.dialogBodyText}>
          「{revokeTarget?.display_name}」（末尾 {revokeTarget?.key_last4}）を
          即時無効化します。連携している外部システムからの呼び出しは即座に 401 を返します。
        </Typography>
        {revokeError && <Box sx={styles.dialogErrorBox}>{revokeError}</Box>}
        <Box sx={styles.dialogActions}>
          <Button
            onClick={closeRevokeDialog}
            disabled={submitting}
            sx={[tertiaryButtonBackgroundColor, styles.dialogActionButton]}
          >
            キャンセル
          </Button>
          <Button
            onClick={handleRevoke}
            disabled={submitting}
            startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : undefined}
            sx={[primaryButtonBackgroundColor, styles.dialogActionButton]}
          >
            無効化する
          </Button>
        </Box>
      </Dialog>
    </Box>
  );
};

export default ApiKeyManagement;
